import React, { useState, useRef, useEffect } from 'react';
import './Features.css';

const tabData = [
  {
    id: 'transcription',
    title: 'AI-Powered Transcription',
    content: {
      heading: 'AI-Powered Transcription',
      description: 'Automatically converts doctor-patient conversations into accurate medical notes — with speaker recognition.',
      technologies: [
        {
          icon: '/images/NLP-front-page.png',
          title: 'Natural Language Processing'
        },
        {
          icon: '/images/multisupportaudio.png',
          title: 'Automatic Speech Recognition'
        },
        {
          icon: '/images/optimization.png',
          title: 'Medical vocabulary optimization'
        }
      ],
      benefits: [
        'Cuts down manual note-taking',
        'Captures every clinical detail',
        'Reduces errors from memory'
      ],
      image: '/images/Transcription.png'
    }
  },
  {
    id: 'translation',
    title: 'Multilingual Translation',
    content: {
      heading: 'Multilingual Translation',
      description: 'Real-time speech and text translation for seamless conversations — accurate, fast, and built for medical vocabulary.',
      technologies: [
        {
          icon: '/images/texttrans.png',
          title: 'Real-Time Speech-to-Speech Translation'
        },
        {
          icon: '/images/speecttext.png',
          title: 'Real-Time Speech-to-Text Translation'
        },
        {
          icon: '/images/medical-prot.png',
          title: 'Medical Terminology-Aware Translation Engine'
        }
      ],
      benefits: [
        'Communicate confidently with patients of any language',
        'Reduce misunderstandings in diagnosis and care',
        'Ensure culturally appropriate and medically precise conversations'
      ],
      image: '/images/lappy.png'
    }
  },
  {
    id: 'integration',
    title: 'EHR & NHS Integration',
    content: {
      heading: 'EHR & NHS Integration',
      description: 'Secure, standards-compliant integration with NHS and EHR platforms — no copy-paste, just seamless data flow.',
      technologies: [
        {
          icon: '/images/HL7.png',
          title: 'HL7 / FHIR APIs for Fast Interoperability'
        },
        {
          icon: '/images/Encryption.png',
          title: 'Secure Encryption & Consent Protocols'
        }
      ],
      benefits: [
        'Automatically syncs notes with NHS systems',
        'Protects patient data with enterprise-grade security',
        'Saves time and improves workflow for every provider'
      ],
      image: '/images/EHR.png'
    }
  }
];

const Features = () => {
  const [activeTab, setActiveTab] = useState('transcription');
  const sectionRef = useRef(null);
  const activeContent = tabData.find(tab => tab.id === activeTab)?.content;
  const [slideClass, setSlideClass] = useState('');

  // Auto-slide logic
  useEffect(() => {
    const tabIds = tabData.map(tab => tab.id);
    const interval = setInterval(() => {
      setActiveTab(prev => {
        const idx = tabIds.indexOf(prev);
        return tabIds[(idx + 1) % tabIds.length];
      });
    }, 3500);
    return () => clearInterval(interval);
  }, [activeTab]);

  // Slide-in effect on tab change
  useEffect(() => {
    setSlideClass('');
    const timeout = setTimeout(() => setSlideClass('slide-in'), 10);
    return () => clearTimeout(timeout);
  }, [activeTab]);

  return (
    <section 
      className="features-section"
      ref={sectionRef}
    >
      <div className="features-container">
        <div className="features-header">
          <h2>More Than a Voice Assistant</h2>
          <h3>A Complete AI Healthcare partner</h3>
        </div>

        <div className="features-tabs">
          {tabData.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.title}
            </button>
          ))}
        </div>

        <div className="features-content">
          {/* Section Progress Indicator */}
          <div className="section-progress">
            <div className="progress-dots">
              <div className="progress-line"></div>
              {tabData.map((tab) => (
                <div
                  key={tab.id}
                  className={`progress-dot ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                />
              ))}
            </div>
          </div>

          <div className="features-image">
            <div className="phone-mockup">
              <img src={activeContent?.image} alt="Feature preview" />
            </div>
          </div>

          <div className="features-info">
            <div className={`tab-content ${slideClass}`}>
              <h3>{activeContent?.heading}</h3>
              <p className="description">{activeContent?.description}</p>

              <div className="technology-section">
                <h4>Technology Used -</h4>
                <div className="technology-grid">
                  {activeContent?.technologies.map((tech, index) => (
                    <div key={index} className="technology-item">
                      <div className="tech-icon">
                        <img src={tech.icon} alt={tech.title} />
                      </div>
                      <span>{tech.title}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="benefits-section">
                <h4>Benefits-</h4>
                <ul>
                  {activeContent?.benefits.map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;